import React, { useCallback, useEffect, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import bmsService, { BmsCustomer } from "../../features/bms/bmsService";
import { TbUsers, TbEdit, TbCheck, TbX } from "react-icons/tb";
import BmsChatbotSelector from "../../components/bms/BmsChatbotSelector";
import BmsPagination from "../../components/bms/BmsPagination";
import { showError, showSuccess } from "../../utils/bmsToast";

const PAGE_SIZE = 25;

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const BmsCustomersPage: React.FC = () => {
  const { t } = useTranslation();
  const [chatbotId, setChatbotId] = useState<number | null>(null);
  const [data, setData] = useState<BmsCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);

  const [editCustomer, setEditCustomer] = useState<BmsCustomer | null>(null);
  const [editForm, setEditForm] = useState({ name: "", surname: "", phone: "", email: "", status: "active" });
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    if (!chatbotId) { setData([]); setTotal(0); return; }
    setIsLoading(true);
    try {
      const res = await bmsService.listCustomers(chatbotId, { limit, skip });
      setData(res.data || []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Failed to load customers");
    } finally {
      setIsLoading(false);
    }
  }, [chatbotId, limit, skip]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [chatbotId]);

  const openEdit = (c: BmsCustomer) => {
    setEditCustomer(c);
    setEditForm({ name: c.name || "", surname: c.surname || "", phone: c.phone || "", email: c.email || "", status: c.status || "active" });
  };

  const handleSave = async () => {
    if (!chatbotId || !editCustomer) return;
    setIsSaving(true);
    try {
      await bmsService.updateCustomer(chatbotId, editCustomer.id, {
        name: editForm.name.trim() || undefined, surname: editForm.surname.trim() || undefined,
        phone: editForm.phone.trim() || undefined, email: editForm.email.trim() || undefined, status: editForm.status,
      });
      showSuccess(t("bms_customer_updated") || "Customer updated");
      setEditCustomer(null);
      load();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Failed to update");
    } finally { setIsSaving(false); }
  };

  return (
    <div className="chatbots-page bms-page">
      <div className="page-header-card mb-3">
        <div className="page-header-text">
          <h1 className="d-flex align-items-center gap-2"><TbUsers /> {t("bms_customers")}</h1>
          <p>{t("bms_customers_desc")}</p>
        </div>
      </div>

      <div className="p-3 pt-0">
        <BmsChatbotSelector chatbotId={chatbotId} onChatbotChange={setChatbotId} />

        <div className="bms-section-card mt-3">
          <div className="bms-section-header">
            <div className="bms-section-title">
              <span className="bms-section-icon"><TbUsers /></span>
              {t("bms_customers")}
            </div>
            <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{total} {t("records")}</span>
          </div>
          {isLoading ? (
            <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : data.length === 0 ? (
            <div className="bms-empty">
              <div className="bms-empty-icon"><TbUsers /></div>
              <p>{t("bms_no_data")}</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="bms-table table mb-0">
                  <thead>
                    <tr>
                      <th>{t("bms_name")}</th>
                      <th>{t("bms_surname")}</th>
                      <th>{t("bms_phone")}</th>
                      <th>{t("bms_email")}</th>
                      <th>{t("bms_status")}</th>
                      <th>{t("bms_created_at")}</th>
                      <th>{t("bms_updated_at")}</th>
                      <th style={{ width: 60, textAlign: "center" }}>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((c) => (
                      <tr key={c.id}>
                        <td className="fw-semibold">{c.name}</td>
                        <td>{c.surname}</td>
                        <td style={{ fontFamily: "var(--font-mono, monospace)", fontSize: "0.85rem" }}>{c.phone}</td>
                        <td>{c.email || "—"}</td>
                        <td><span className={`bms-status ${c.status === "active" ? "active" : "inactive"}`}>{c.status}</span></td>
                        <td style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>{formatDate(c.created_at)}</td>
                        <td style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>{formatDate(c.updated_at)}</td>
                        <td style={{ textAlign: "center" }}>
                          <button className="bms-action-btn edit" onClick={() => openEdit(c)} title={t("edit")}><TbEdit /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <BmsPagination total={total} skip={skip} limit={limit} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
            </>
          )}
        </div>
      </div>

      <Modal show={!!editCustomer} onHide={() => setEditCustomer(null)} className="bms-modal" centered>
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2"><TbEdit /> {t("bms_edit_customer")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_name")} *</Form.Label><Form.Control value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_surname")}</Form.Label><Form.Control value={editForm.surname} onChange={(e) => setEditForm((f) => ({ ...f, surname: e.target.value }))} /></div>
            <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_phone")} *</Form.Label><Form.Control value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} /></div>
            <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_email")}</Form.Label><Form.Control type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_status")}</Form.Label>
              <Form.Select value={editForm.status} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}>
                <option value="active">{t("active")}</option><option value="inactive">{t("inactive")}</option>
              </Form.Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => setEditCustomer(null)} disabled={isSaving}><TbX /> {t("cancel")}</button>
          <button className="bms-btn-primary" onClick={handleSave} disabled={isSaving}>{isSaving ? <Spinner animation="border" size="sm" /> : <TbCheck />} {t("save")}</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BmsCustomersPage;
